import { CMSComponent, CMSComponents, type CMSComponentProps } from "../engine/CMSComponent.tsx";
import RichText from "../engine/RichText.tsx";
import { isRichText } from "../types.ts";

const headingTags = ["h1", "h2", "h3", "h4", "h5", "h6", "p"] as const;
type HeadingTag = (typeof headingTags)[number];

const tagOf = (value: unknown, fallback: HeadingTag): HeadingTag =>
  headingTags.includes(value as HeadingTag) ? (value as HeadingTag) : fallback;

/** "Headline 3" -> "size-headline-3" */
const sizeClass = (value: unknown) =>
  typeof value === "string" ? `size-${value.toLowerCase().replace(/\s+/g, "-")}` : "";

export default function TextGroup({ node }: CMSComponentProps) {
  const Headline = tagOf(node.headlineTag, "h2");
  const Subheading = tagOf(node.subheadingTag, "p");
  const Eyebrow = tagOf(node.eyebrowTag, "p");
  const alignment = String(node.alignment ?? "start").toLowerCase();

  return (
    <div className={`cms-text-group align-${alignment}`}>
      {typeof node.eyebrowText === "string" && (
        <Eyebrow className="cms-eyebrow">{node.eyebrowText}</Eyebrow>
      )}
      {isRichText(node.headline) && (
        <Headline className={`cms-headline ${sizeClass(node.headlineSize)}`}>
          <RichText document={node.headline} inline />
        </Headline>
      )}
      {isRichText(node.subheading) && (
        <Subheading className="cms-subheading">
          <RichText document={node.subheading} inline />
        </Subheading>
      )}
      {isRichText(node.paragraph) && (
        <div className={`cms-paragraph ${sizeClass(node.paragraphSize)}`}>
          <RichText document={node.paragraph} />
        </div>
      )}
      <CMSComponent node={node.featureList} />
      <CMSComponents nodes={node.collection} />
      <CMSComponent node={node.actionGroup} />
      <CMSComponent node={node.disclosureType} />
    </div>
  );
}

export function FeatureList({ node }: CMSComponentProps) {
  if (!isRichText(node.list)) return null;
  return (
    <div className="cms-feature-list">
      <RichText document={node.list} />
    </div>
  );
}

export function Disclosure({ node }: CMSComponentProps) {
  return (
    <div className="cms-disclosure">
      {isRichText(node.paragraph) && <RichText document={node.paragraph} />}
      <CMSComponents nodes={node.collection} />
    </div>
  );
}

export function DisclosureItem({ node }: CMSComponentProps) {
  return isRichText(node.text) ? <RichText document={node.text} /> : null;
}

export function DataItem({ node }: CMSComponentProps) {
  const number = (node.numberGroup as { number1?: Record<string, unknown> })?.number1;
  return (
    <div className="cms-data-item">
      {number && (
        <p className="cms-data-item__number">
          {String(number.prefix ?? "")}
          {isRichText(number.number) && <RichText document={number.number} inline />}
          {String(number.suffix ?? "")}
        </p>
      )}
      {isRichText(node.text) && <RichText document={node.text} />}
    </div>
  );
}
